<template>
  <div
    class="box product-box"
    :class="{ 'is-editing': edit }"
    id="products-box-container"
  >
    <!-- Imagen ampliada desde la miniatura de la tabla -->
    <div
      v-if="imagePreview"
      ref="imagePreview"
      class="image-preview-overlay"
      tabindex="-1"
      @click.self="imagePreview = null"
      @keydown.esc.stop="imagePreview = null"
    >
      <div class="image-preview-dialog">
        <div class="image-preview-header">
          <div class="image-preview-title">
            <span class="image-preview-code">{{ imagePreview.code }}</span>
            <span class="image-preview-description">
              {{ imagePreview.description }}
            </span>
          </div>
          <button
            type="button"
            class="image-preview-close"
            :title="$t('products_box.close')"
            @click="imagePreview = null"
          >
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <img
          :src="imagePreview.src"
          :alt="imagePreview.code"
          class="image-preview-img"
        />
      </div>
    </div>

    <div class="box-header products-header">
      <div class="products-title">
        <h6 class="title">{{ $t("products_box.products") }}</h6>
        <span class="stat-chip">
          {{ edit ? liveLines : productsLines }}
          {{ $t("products_box.lines") }}
        </span>
        <span class="stat-chip">
          {{ edit ? liveQty : productsQty }} {{ $t("products_box.pcs") }}
        </span>
        <span v-if="edit" class="editing-chip">
          <i class="mdi mdi-pencil"></i>
          {{ $t("products_box.editing") }}
        </span>
      </div>
      <div class="products-header-actions" v-if="canBeEdited">
        <button
          v-if="!edit"
          type="button"
          class="pb-btn is-primary"
          @click="startEdit()"
        >
          <i class="mdi mdi-pencil"></i>
          {{ $t("products_box.edit") }}
        </button>
        <button
          v-else
          type="button"
          class="pb-btn is-primary"
          :disabled="loading"
          @click="openAddProduct"
        >
          <i class="mdi mdi-plus"></i>
          {{ $t("products_box.add_product") }}
        </button>
      </div>
    </div>
    <div class="content" id="products-box">
      <div class="table-full">
        <!-- Product List -->
        <div class="desktop-products-tools" id="desktop-products-tools">
          <div class="desktop-filter-wrap">
            <div class="desktop-filter-input-wrap">
              <i class="mdi mdi-magnify filter-icon"></i>
              <input
                id="nkr-code-filter"
                v-model.trim="nkrCodeFilter"
                type="text"
                class="desktop-filter-input"
                :placeholder="$t('products_box.filter_nkrcode')"
              />
              <button
                v-if="nkrCodeFilter"
                type="button"
                class="desktop-filter-clear"
                :title="$t('products_box.clear')"
                @click="nkrCodeFilter = ''"
              >
                <i class="mdi mdi-close"></i>
              </button>
            </div>
          </div>
          <span v-if="nkrCodeFilter" class="filter-count">
            {{
              $t("products_box.showing_of", {
                shown: filteredAndSortedProducts.length,
                total: editingProducts.length
              })
            }}
          </span>
        </div>

        <div class="desktop-products-table" id="desktop-products-table">
          <table class="desktop-table">
            <thead>
              <tr>
                <th
                  class="sortable-col col-num"
                  @click="sortBy('order')"
                  :title="$t('products_box.sort_by_order')"
                >
                  #
                  <i :class="getSortIcon('order')"></i>
                </th>
                <th
                  class="sortable-col col-code"
                  @click="sortBy('code')"
                  :title="$t('products_box.sort_by_code')"
                >
                  {{ $t("products_box.code") }}
                  <i :class="getSortIcon('code')"></i>
                </th>
                <th
                  class="sortable-col"
                  @click="sortBy('description')"
                  :title="$t('products_box.sort_by_description')"
                >
                  {{ $t("products_box.description") }}
                  <i :class="getSortIcon('description')"></i>
                </th>
                <th
                  class="sortable-col col-brand"
                  @click="sortBy('brand')"
                  :title="$t('products_box.sort_by_brand')"
                >
                  {{ $t("products_box.brand") }}
                  <i :class="getSortIcon('brand')"></i>
                </th>
                <th
                  class="sortable-col col-line"
                  @click="sortBy('line')"
                  :title="$t('products_box.sort_by_line')"
                >
                  {{ $t("products_box.line") }}
                  <i :class="getSortIcon('line')"></i>
                </th>
                <th
                  class="text-right sortable-col col-price"
                  @click="sortBy('price')"
                >
                  {{ $t("products_box.price") }}
                  <i :class="getSortIcon('price')"></i>
                </th>
                <th
                  class="text-right sortable-col col-qty"
                  @click="sortBy('quantity')"
                >
                  {{ $t("products_box.qty") }}
                  <i :class="getSortIcon('quantity')"></i>
                </th>
                <th v-if="hasCouponLines" class="text-right col-discount">
                  {{ $t("products_box.discount") }}
                  <span class="coupon-head-code">{{ couponCode }}</span>
                </th>
                <th
                  class="text-right sortable-col col-total"
                  @click="sortBy('total')"
                >
                  {{ $t("products_box.total") }}
                  <i :class="getSortIcon('total')"></i>
                </th>
                <th class="col-actions">
                  <span class="sr-only">{{ $t("products_box.actions") }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in filteredAndSortedProducts"
                :key="
                  item.productId +
                    '-' +
                    item.warehouseId +
                    '-desktop-' +
                    renderId
                "
                :class="{
                  'out-of-stock-row':
                    item.stockOnConfirmation !== null &&
                    item.stockOnConfirmation !== undefined &&
                    item.stockOnConfirmation - item.quantity < 0,
                  'is-new': item === lastAddedItem,
                  'is-core-row': item.coreOf
                }"
              >
                <td class="text-center text-gray-500">
                  {{ productOrderMap.get(item.productId) }}
                </td>
                <td class="product-code-cell">
                  <div class="desktop-code-wrap">
                    <button
                      v-if="item.image"
                      type="button"
                      class="desktop-thumb"
                      :title="$t('products_box.view_image')"
                      @click="openImagePreview(item)"
                    >
                      <img
                        :src="item.image"
                        :alt="getProductCode(item)"
                        loading="lazy"
                        @error="
                          $event.target.parentElement.style.display = 'none'
                        "
                      />
                    </button>
                    <div class="desktop-code">{{ getProductCode(item) }}</div>
                  </div>
                </td>
                <td>
                  <div class="desktop-description">
                    {{ item.description || "-" }}
                  </div>
                  <div
                    v-if="item.coreOf || item.coreNkrCode"
                    class="core-link"
                    :class="{ 'is-core': item.coreOf }"
                  >
                    <i class="mdi mdi-link-variant"></i>
                    <template v-if="item.coreOf">
                      {{ $t("products_box.core_of", { code: item.coreOf }) }}
                    </template>
                    <template v-else>
                      {{
                        $t("products_box.has_core", { code: item.coreNkrCode })
                      }}
                    </template>
                  </div>
                  <div
                    v-if="
                      item.stockOnConfirmation !== null &&
                        item.stockOnConfirmation !== undefined
                    "
                    class="desktop-stock-on-conf"
                    :class="{
                      danger: item.stockOnConfirmation - item.quantity < 0
                    }"
                  >
                    {{ $t("products_box.stock_on_conf") }}:
                    {{ item.stockOnConfirmation }}
                  </div>
                  <div
                    v-if="
                      item.stockOnConfirmation == 0 &&
                        hasStockInOtherWarehouses(item)
                    "
                    class="mt-1 px-2 py-1 bg-blue-50 border border-blue-300 rounded text-xs text-blue-700"
                  >
                    <i class="mdi mdi-information"></i>
                    {{ $t("products_box.available_in") }}
                    <span
                      v-for="(wh, idx) in getOtherWarehousesWithStock(item)"
                      :key="wh.code"
                      class="font-medium"
                    >
                      {{ formatOtherWarehouseStockLabel(wh, idx, item) }}
                    </span>
                  </div>
                </td>
                <td>
                  <div class="desktop-brand">
                    {{ item.brand || $t("products_box.na") }}
                  </div>
                </td>
                <td>
                  <div class="desktop-line">{{ item.line || "-" }}</div>
                  <div v-if="item.warehouseCode" class="desktop-warehouse">
                    {{ item.warehouseCode }}
                  </div>
                  <div
                    v-if="item.isTransfer && item.sourceDepartmentCode"
                    class="desktop-transfer"
                  >
                    {{ $t("products_box.from") }}
                    {{ item.sourceDepartmentCode }}
                  </div>
                </td>
                <td class="text-right">
                  {{ item.price | currencyFormatted(true) }}
                </td>
                <td class="text-right">
                  <div v-if="edit" class="qty-inline-control">
                    <button
                      type="button"
                      class="qty-btn"
                      @click="decrementQty(item)"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      class="qty-inline-input"
                      :value="item.quantity"
                      @input="setQty(item, $event.target.value)"
                      @change="commitQty(item, $event)"
                      @click.stop
                    />
                    <button
                      type="button"
                      class="qty-btn"
                      @click="incrementQty(item)"
                    >
                      +
                    </button>
                  </div>
                  <template v-else>{{ item.quantity }}</template>
                </td>
                <td v-if="hasCouponLines" class="text-right">
                  <span
                    v-if="couponDiscount(item)"
                    class="coupon-discount-cell"
                  >
                    -{{ couponDiscount(item) | currencyFormatted(true) }}
                  </span>
                  <span v-else class="text-gray-300">—</span>
                </td>
                <td class="text-right font-semibold text-green-700">
                  {{ couponLineTotal(item) | currencyFormatted(true) }}
                </td>
                <td>
                  <div class="desktop-actions">
                    <button
                      @click="$emit('viewProduct', getProductCode(item))"
                      class="action-btn view-btn"
                      :title="$t('products_box.view_details')"
                    >
                      <i class="mdi mdi-eye"></i>
                      <span class="action-text">{{
                        $t("products_box.view")
                      }}</span>
                    </button>
                    <button
                      v-if="edit"
                      @click="openEditModal(item, 'product')"
                      class="action-btn edit-btn"
                      :title="$t('products_box.edit')"
                    >
                      <i class="mdi mdi-pencil"></i>
                      <span class="action-text">{{
                        $t("products_box.edit")
                      }}</span>
                    </button>
                    <button
                      v-if="edit"
                      @click="removeItemById(item.productId)"
                      class="action-btn delete-btn"
                      :title="$t('products_box.remove')"
                    >
                      <i class="mdi mdi-delete"></i>
                      <span class="action-text">{{
                        $t("products_box.remove")
                      }}</span>
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="filteredAndSortedProducts.length === 0">
                <td
                  :colspan="hasCouponLines ? 10 : 9"
                  class="desktop-empty-results"
                >
                  <i
                    class="mdi mdi-package-variant-closed desktop-empty-icon"
                  ></i>
                  <div>
                    {{
                      nkrCodeFilter.trim()
                        ? $t("products_box.no_products_found")
                        : $t("products_box.no_products_in_order")
                    }}
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="filteredAndSortedProducts.length > 0">
              <tr class="desktop-total-row">
                <td colspan="6" class="text-right font-semibold">
                  {{ $t("products_box.total") }}
                </td>
                <td class="text-right font-semibold">
                  {{ visibleProductsQty }}
                </td>
                <td v-if="hasCouponLines" class="text-right">
                  <span class="coupon-discount-cell">
                    -{{
                      visibleCouponDiscount.toFixed(2) | currencyFormatted(true)
                    }}
                  </span>
                </td>
                <td class="text-right font-bold text-green-700">
                  {{
                    (visibleProductsTotal - visibleCouponDiscount).toFixed(2)
                      | currencyFormatted(true)
                  }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="products-list mobile-products-list" id="products-list">
          <div
            v-for="item in filteredAndSortedProducts"
            :key="item.productId + '-' + item.warehouseId + '-card'"
            :id="'product-row-' + item.productId + '-' + item.warehouseId"
            class="product-row"
            :class="{
              'is-new': item === lastAddedItem,
              'is-core-row': item.coreOf
            }"
            :style="
              item.stockOnConfirmation !== null &&
              item.stockOnConfirmation !== undefined &&
              item.stockOnConfirmation - item.quantity < 0
                ? 'background-color: #fee2e2;'
                : ''
            "
          >
            <!-- Product Image -->
            <div class="product-image" id="product-image">
              <img
                v-if="item.image"
                :src="item.image"
                :alt="item.nkrCode || item.sku"
                class="w-full h-full object-contain"
                @error="$event.target.style.display = 'none'"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400"
              >
                <i class="mdi mdi-image-off text-2xl"></i>
              </div>
            </div>

            <!-- Product Info -->
            <div class="product-info" id="product-info">
              <div class="product-header">
                <span class="text-xs text-gray-400 font-medium mr-1">
                  #{{ productOrderMap.get(item.productId) }}
                </span>
                <span class="product-sku">{{ item.nkrCode || item.sku }}</span>
                <span class="product-brand">{{
                  item.brand || $t("products_box.na")
                }}</span>
              </div>
              <div class="product-description">{{ item.description }}</div>
              <div
                v-if="item.coreOf || item.coreNkrCode"
                class="core-link"
                :class="{ 'is-core': item.coreOf }"
              >
                <i class="mdi mdi-link-variant"></i>
                <template v-if="item.coreOf">
                  {{ $t("products_box.core_of", { code: item.coreOf }) }}
                </template>
                <template v-else>
                  {{ $t("products_box.has_core", { code: item.coreNkrCode }) }}
                </template>
              </div>
              <div class="product-line">
                {{ $t("products_box.line") }}
                <span class="text-blue-600 font-semibold">{{ item.line }}</span>
                <span v-if="item.warehouseCode" class="ml-2 text-gray-500">
                  {{ item.warehouseCode }}
                </span>
                <span
                  v-if="item.isTransfer && item.sourceDepartmentCode"
                  class="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded font-medium"
                >
                  <i class="mdi mdi-truck-fast mr-1"></i>
                  {{ $t("products_box.from") }} {{ item.sourceDepartmentCode }}
                </span>
              </div>
              <div
                class="mt-1 text-xs"
                :class="
                  item.stockOnConfirmation - item.quantity < 0
                    ? 'text-red-700 font-medium'
                    : 'text-gray-500'
                "
                v-if="
                  item.stockOnConfirmation !== null &&
                    item.stockOnConfirmation !== undefined
                "
              >
                {{ $t("products_box.stock_on_conf") }}:
                {{ item.stockOnConfirmation }}
              </div>
              <div
                v-if="
                  item.stockOnConfirmation === 0 &&
                    hasStockInOtherWarehouses(item)
                "
                class="mt-2 px-2 py-1 bg-blue-50 border border-blue-300 rounded text-xs text-blue-700"
              >
                <i class="mdi mdi-information"></i>
                {{ $t("products_box.available_in") }}
                <span
                  v-for="(wh, idx) in getOtherWarehousesWithStock(item)"
                  :key="wh.code"
                  class="font-medium"
                >
                  {{ formatOtherWarehouseStockLabel(wh, idx, item) }}
                </span>
              </div>
            </div>

            <!-- Price/Qty Section -->
            <div class="product-pricing" id="product-pricing">
              <div class="price-item">
                <span class="price-label">{{ $t("products_box.price") }}</span>
                <span class="price-value">{{
                  item.price | currencyFormatted(true)
                }}</span>
              </div>
              <div class="price-item">
                <span class="price-label">{{ $t("products_box.qty") }}</span>
                <div v-if="edit" class="qty-inline-control">
                  <button
                    type="button"
                    class="qty-btn"
                    @click="decrementQty(item)"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    class="qty-inline-input"
                    :value="item.quantity"
                    @input="setQty(item, $event.target.value)"
                    @change="commitQty(item, $event)"
                    @click.stop
                  />
                  <button
                    type="button"
                    class="qty-btn"
                    @click="incrementQty(item)"
                  >
                    +
                  </button>
                </div>
                <span v-else class="price-value">{{ item.quantity }}</span>
              </div>
              <div v-if="couponDiscount(item)" class="price-item">
                <span class="price-label"
                  >{{ $t("products_box.discount") }} {{ couponCode }}</span
                >
                <span class="price-value coupon-discount-cell"
                  >-{{ couponDiscount(item) | currencyFormatted(true) }}</span
                >
              </div>
              <div class="price-item total">
                <span class="price-label">{{ $t("products_box.total") }}</span>
                <span class="price-value">{{
                  couponLineTotal(item) | currencyFormatted(true)
                }}</span>
              </div>
            </div>

            <!-- Actions -->
            <div class="product-actions" id="product-actions">
              <button
                @click="$emit('viewProduct', item.nkrCode || item.sku)"
                class="action-btn view-btn"
                :title="$t('products_box.view_details')"
              >
                <i class="mdi mdi-eye"></i>
                <span class="action-text">{{ $t("products_box.view") }}</span>
              </button>
              <button
                v-if="edit"
                @click="openEditModal(item, 'product')"
                class="action-btn edit-btn"
                :title="$t('products_box.edit')"
              >
                <i class="mdi mdi-pencil"></i>
                <span class="action-text">{{ $t("products_box.edit") }}</span>
              </button>
              <button
                v-if="edit"
                @click="removeItemById(item.productId)"
                class="action-btn delete-btn"
                :title="$t('products_box.remove')"
              >
                <i class="mdi mdi-delete"></i>
                <span class="action-text">{{ $t("products_box.remove") }}</span>
              </button>
            </div>
          </div>

          <!-- Mobile Products Total -->
          <div
            v-if="filteredAndSortedProducts.length > 0"
            class="products-total-row"
          >
            <span class="products-total-label">
              {{ $t("products_box.total") }}
              ({{ visibleProductsQty }} {{ $t("products_box.pcs") }})
            </span>
            <span class="products-total-value">
              <span v-if="hasCouponLines" class="coupon-discount-cell mr-2">
                -{{
                  visibleCouponDiscount.toFixed(2) | currencyFormatted(true)
                }}
              </span>
              {{
                (visibleProductsTotal - visibleCouponDiscount).toFixed(2)
                  | currencyFormatted(true)
              }}
            </span>
          </div>
        </div>

        <!-- Kit Products -->
        <template v-if="editingProductsKits.length > 0">
          <div class="kits-section" id="kits-section">
            <h6 class="kits-title">{{ $t("products_box.kits_in_order") }}</h6>
            <div class="products-list" id="kits-list">
              <div
                v-for="kit in editingProductsKits"
                :key="kit.kitCode + '-kit-card'"
                :id="'kit-row-' + kit.kitCode"
                class="product-row kit-row"
              >
                <!-- Kit Image -->
                <div class="product-image">
                  <img
                    v-if="kit.kitImage"
                    :src="kit.kitImage"
                    :alt="kit.kitCode"
                    class="w-full h-full object-contain"
                    @error="$event.target.style.display = 'none'"
                  />
                  <div
                    v-else
                    class="w-full h-full flex items-center justify-center bg-purple-100 text-purple-400"
                  >
                    <i class="mdi mdi-package-variant text-2xl"></i>
                  </div>
                </div>

                <!-- Kit Info -->
                <div class="product-info">
                  <div class="product-header">
                    <span class="product-sku kit-badge">
                      <i class="mdi mdi-package-variant mr-1"></i>
                      {{ kit.kitCode }}
                    </span>
                  </div>
                  <div class="product-description">{{ kit.description }}</div>
                  <!-- Kit Items Preview -->
                  <div class="kit-items-preview">
                    <span class="kit-items-label">{{
                      $t("products_box.contains")
                    }}</span>
                    <span
                      v-for="(kitItem, index) in kit.kitItems.slice(0, 3)"
                      :key="index"
                      class="kit-item-tag"
                    >
                      {{ kitItem.nkrCode }} ({{ kitItem.quantity }})
                    </span>
                    <span v-if="kit.kitItems.length > 3" class="kit-items-more">
                      +{{ kit.kitItems.length - 3 }}
                      {{ $t("products_box.more") }}
                    </span>
                  </div>
                </div>

                <!-- Price/Qty Section -->
                <div class="product-pricing">
                  <div class="price-item">
                    <span class="price-label">{{
                      $t("products_box.price")
                    }}</span>
                    <span class="price-value">{{
                      kitUnitPrice(kit) | currencyFormatted(true)
                    }}</span>
                  </div>
                  <div class="price-item">
                    <span class="price-label">{{
                      $t("products_box.qty")
                    }}</span>
                    <span class="price-value">{{ kit.quantity }}</span>
                  </div>
                  <div class="price-item total">
                    <span class="price-label">{{
                      $t("products_box.total")
                    }}</span>
                    <span class="price-value">{{
                      (kitUnitPrice(kit) * kit.quantity).toFixed(2)
                        | currencyFormatted(true)
                    }}</span>
                  </div>
                </div>

                <!-- Actions -->
                <div class="product-actions">
                  <button
                    @click="$emit('viewProduct', kit.kitCode)"
                    class="action-btn view-btn"
                    :title="$t('products_box.view_kit')"
                  >
                    <i class="mdi mdi-eye"></i>
                    <span class="action-text">{{
                      $t("products_box.view")
                    }}</span>
                  </button>
                  <button
                    v-if="edit"
                    @click="openEditModal(kit, 'kit')"
                    class="action-btn edit-btn"
                    :title="$t('products_box.edit')"
                  >
                    <i class="mdi mdi-pencil"></i>
                    <span class="action-text">{{
                      $t("products_box.edit")
                    }}</span>
                  </button>
                  <button
                    v-if="edit"
                    @click="removeKit(kit.kitCode)"
                    class="action-btn delete-btn"
                    :title="$t('products_box.remove')"
                  >
                    <i class="mdi mdi-delete"></i>
                    <span class="action-text">{{
                      $t("products_box.remove")
                    }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Add Product Section: una linea al final de la lista; el mismo
             buscador se abre desde el boton de la cabecera -->
        <div class="add-product-section" id="add-product-section">
          <NewProductLine
            v-if="editingProducts && !loading"
            ref="newProductLine"
            :edit="edit"
            :priceLevel="priceLevel"
            :products="editingProducts"
            :departmentCode="orderDepartmentCode"
            :compactTrigger="true"
            @addProduct="addProduct"
            @addKit="addKit"
            @item-selected="scrollToBottom"
          />
        </div>
      </div>

      <!-- Barra de guardado: fija abajo mientras se edita -->
      <div v-if="edit" class="edit-action-bar" id="action-buttons-container">
        <div class="edit-summary">
          <!-- Con cambios, el estado abre el resumen de lo que se va a guardar -->
          <button
            v-if="pendingChangesCount > 0"
            type="button"
            class="changes-status has-changes is-button"
            @click="showChangesPreview = true"
          >
            <i class="mdi mdi-circle-medium"></i>
            {{
              $tc("products_box.unsaved_changes", pendingChangesCount, {
                count: pendingChangesCount
              })
            }}
            <span class="view-changes">
              <i class="mdi mdi-eye-outline"></i>
              {{ $t("products_box.view_changes") }}
            </span>
          </button>
          <span v-else class="changes-status">
            <i class="mdi mdi-check-circle-outline"></i>
            {{ $t("products_box.no_changes") }}
          </span>
          <span class="summary-totals">
            {{ liveLines }} {{ $t("products_box.lines") }} · {{ liveQty }}
            {{ $t("products_box.pcs") }} ·
            <strong>{{
              liveTotal.toFixed(2) | currencyFormatted(true)
            }}</strong>
          </span>
        </div>
        <div class="edit-buttons">
          <button
            v-if="!loading"
            type="button"
            class="pb-btn"
            @click="revertChanges()"
          >
            <i class="mdi mdi-close"></i>
            <span class="label-long">
              {{ $t("products_box.discard_changes") }}
            </span>
            <span class="label-short">{{ $t("products_box.discard") }}</span>
          </button>
          <button
            type="button"
            class="pb-btn is-success"
            :disabled="loading"
            @click="saveChanges()"
          >
            <i
              class="mdi"
              :class="loading ? 'mdi-loading mdi-spin' : 'mdi-check'"
            ></i>
            <template v-if="loading">{{ $t("products_box.saving") }}</template>
            <template v-else>
              <span class="label-long">
                {{ $t("products_box.save_changes") }}
              </span>
              <span class="label-short">{{ $t("common.save") }}</span>
            </template>
          </button>
        </div>
      </div>
    </div>

    <!-- Resumen de cambios antes de guardar -->
    <BaseModal
      v-if="showChangesPreview && changesPreview"
      :value="true"
      id="products-changes-modal"
      @input="showChangesPreview = false"
    >
      <template v-slot:header>
        {{ $t("products_box.changes_title") }}
      </template>
      <div class="changes-preview">
        <section
          v-for="group in changesPreview.groups"
          :key="group.kind"
          class="changes-group"
          :class="`is-${group.kind}`"
        >
          <h3 class="changes-group-title">
            <i class="mdi" :class="group.icon"></i>
            {{ $t(group.label) }}
            <span class="changes-group-count">{{ group.lines.length }}</span>
          </h3>
          <div v-for="line in group.lines" :key="line.key" class="change-line">
            <div class="change-line-main">
              <span class="change-code">{{ line.code }}</span>
              <span v-if="line.isKit" class="change-kit">KIT</span>
              <span class="change-description">{{ line.description }}</span>
            </div>
            <div class="change-line-detail">
              <span :class="{ 'is-changed': line.qtyChanged }">
                {{ $t("products_box.qty") }}:
                <template v-if="line.qtyChanged">
                  <s>{{ line.before.qty }}</s> →
                </template>
                <strong>{{ line.shown.qty }}</strong>
              </span>
              <span :class="{ 'is-changed': line.priceChanged }">
                {{ $t("products_box.price") }}:
                <template v-if="line.priceChanged">
                  <s>{{ line.before.price | currencyFormatted(true) }}</s> →
                </template>
                <strong>{{
                  line.shown.price | currencyFormatted(true)
                }}</strong>
              </span>
              <span class="change-subtotal">
                {{
                  (line.shown.qty * line.shown.price).toFixed(2)
                    | currencyFormatted(true)
                }}
              </span>
            </div>
          </div>
        </section>

        <div class="changes-totals">
          <div>
            <span>{{ $t("products_box.total_before") }}</span>
            <strong>{{
              changesPreview.totalBefore.toFixed(2) | currencyFormatted(true)
            }}</strong>
          </div>
          <div>
            <span>{{ $t("products_box.total_after") }}</span>
            <strong>
              {{ liveTotal.toFixed(2) | currencyFormatted(true) }}
              <em
                class="changes-diff"
                :class="changesPreview.diff >= 0 ? 'is-up' : 'is-down'"
              >
                {{ changesPreview.diff >= 0 ? "+" : "−"
                }}{{
                  Math.abs(changesPreview.diff).toFixed(2)
                    | currencyFormatted(true)
                }}
              </em>
            </strong>
          </div>
        </div>

        <div class="changes-actions">
          <button
            type="button"
            class="pb-btn"
            @click="showChangesPreview = false"
          >
            {{ $t("products_box.keep_editing") }}
          </button>
          <button
            type="button"
            class="pb-btn is-success"
            :disabled="loading"
            @click="
              showChangesPreview = false;
              saveChanges();
            "
          >
            <i class="mdi mdi-check"></i>
            {{ $t("products_box.save_changes") }}
          </button>
        </div>
      </div>
    </BaseModal>

    <!-- Mobile Edit Modal -->
    <div
      v-if="showEditModal"
      ref="editModal"
      id="edit-modal-overlay"
      class="mobile-edit-modal-overlay"
    >
      <div
        class="mobile-edit-modal-content"
        :class="{ 'has-lines': editModalLineOptions.length > 1 }"
        id="edit-modal-content"
      >
        <!-- Header -->
        <div
          id="edit-modal-header"
          class="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-blue-600 text-white"
        >
          <h3 class="text-base font-semibold">
            {{
              editModalType === "kit"
                ? $t("products_box.edit_kit")
                : $t("products_box.edit_product")
            }}
          </h3>
          <button
            @click="cancelEditModal"
            class="w-8 h-8 flex items-center justify-center rounded-full hover:bg-blue-700"
          >
            <i class="mdi mdi-close text-xl"></i>
          </button>
        </div>

        <!-- Product/Kit Info -->
        <div
          class="px-4 py-2 bg-gray-50 border-b"
          id="edit-modal-info"
          v-if="editModalItem"
        >
          <div class="text-sm font-semibold text-gray-900">
            {{
              editModalType === "kit"
                ? editModalItem.kitCode
                : editModalItem.nkrCode || editModalItem.sku
            }}
          </div>
          <div class="text-sm text-gray-600 truncate">
            {{ editModalItem.description }}
          </div>
          <div class="text-xs text-gray-500" v-if="editModalType !== 'kit'">
            {{ $t("products_box.line") }} {{ editModalItem.line }} •
            {{ editModalItem.brand || $t("products_box.na") }}
          </div>
        </div>

        <!-- Edit Form -->
        <div
          class="px-4 py-3 space-y-3 flex-1 overflow-auto"
          id="edit-modal-form"
          v-if="editModalItem"
        >
          <!-- Linea y precio: en pantalla grande van en dos columnas -->
          <div
            v-if="editModalType === 'product'"
            class="edit-modal-top"
            :class="{ 'has-lines': editModalLineOptions.length > 1 }"
          >
            <!-- Linea: otra linea del mismo producto que no este ya en la orden -->
            <div v-if="editModalLineOptions.length > 1" id="edit-modal-lines">
              <label class="block text-xs font-medium text-gray-700 mb-1">
                <i class="mdi mdi-swap-horizontal mr-1"></i>
                {{ $t("products_box.change_line") }}
              </label>
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <button
                  v-for="line in editModalLineOptions"
                  :key="`${line.productId}-${line.warehouseId}`"
                  type="button"
                  :disabled="!!line.blocked"
                  @click="selectEditModalLine(line)"
                  class="price-option-btn"
                  :class="{
                    'is-selected': isEditModalLine(line),
                    'is-blocked': line.blocked
                  }"
                >
                  <span class="price-label">
                    {{ line.line }} •
                    {{ line.warehouse || line.departamentoCodigo }}
                  </span>
                  <span class="price-amount">
                    {{ currency }}{{ line[transformedPriceLevel] }}
                  </span>
                  <span class="price-min">
                    {{ $t("products_box.stock") }}: {{ line.stock }}
                  </span>
                  <span v-if="line.blocked" class="line-blocked">
                    {{ $t(`products_box.line_blocked.${line.blocked}`) }}
                  </span>
                </button>
              </div>
            </div>
            <p
              v-else-if="editModalCanChangeLine"
              id="edit-modal-no-lines"
              class="text-xs text-gray-500"
            >
              <i class="mdi mdi-information-outline mr-1"></i>
              {{ $t("products_box.no_other_lines") }}
            </p>

            <!-- Price (only for products, not kits) -->
            <div v-if="editModalType === 'product'">
              <label class="block text-xs font-medium text-gray-700 mb-1">
                <i class="mdi mdi-currency-usd mr-1"></i>
                {{ $t("products_box.price") }}
              </label>
              <input
                v-model.number="editModalItem.price"
                type="number"
                step="0.01"
                min="0"
                class="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0.00"
              />

              <!-- Price Options Grid -->
              <div
                v-if="currentPriceOption"
                id="price-options-grid"
                class="mt-2"
              >
                <label class="block text-xs font-medium text-gray-500 mb-1">
                  {{ $t("products_box.quick_select_price") }}
                </label>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  <button
                    type="button"
                    @click="editModalItem.price = currentPriceOption.online"
                    class="price-option-btn"
                    :class="{
                      'is-selected': transformedPriceLevel === 'online'
                    }"
                  >
                    <span class="price-label">{{
                      $t("products_box.online")
                    }}</span>
                    <span class="price-amount">
                      {{ currency }}{{ currentPriceOption.online }}
                      <span
                        v-if="currentPriceOption.onlineMinPrice"
                        class="price-min"
                        >({{ currency
                        }}{{ currentPriceOption.onlineMinPrice }})</span
                      >
                    </span>
                  </button>
                  <button
                    type="button"
                    @click="editModalItem.price = currentPriceOption.repairShop"
                    class="price-option-btn"
                    :class="{
                      'is-selected': transformedPriceLevel === 'repairShop'
                    }"
                  >
                    <span class="price-label">{{
                      $t("products_box.repair_shop")
                    }}</span>
                    <span class="price-amount">
                      {{ currency }}{{ currentPriceOption.repairShop }}
                      <span
                        v-if="currentPriceOption.repairShopMinPrice"
                        class="price-min"
                        >({{ currency
                        }}{{ currentPriceOption.repairShopMinPrice }})</span
                      >
                    </span>
                  </button>
                  <button
                    type="button"
                    @click="editModalItem.price = currentPriceOption.wholesale"
                    class="price-option-btn"
                    :class="{
                      'is-selected': transformedPriceLevel === 'wholesale'
                    }"
                  >
                    <span class="price-label">{{
                      $t("products_box.wholesale")
                    }}</span>
                    <span class="price-amount">
                      {{ currency }}{{ currentPriceOption.wholesale }}
                      <span
                        v-if="currentPriceOption.wholesaleMinPrice"
                        class="price-min"
                        >({{ currency
                        }}{{ currentPriceOption.wholesaleMinPrice }})</span
                      >
                    </span>
                  </button>
                  <button
                    v-if="userCanSeePrecio4"
                    type="button"
                    @click="editModalItem.price = currentPriceOption.precio4"
                    class="price-option-btn"
                    :class="{
                      'is-selected': transformedPriceLevel === 'precio4'
                    }"
                  >
                    <span class="price-label">{{
                      $t("products_box.export")
                    }}</span>
                    <span class="price-amount">
                      {{ currency }}{{ currentPriceOption.precio4 }}
                      <span
                        v-if="currentPriceOption.precio4MinPrice"
                        class="price-min"
                        >({{ currency
                        }}{{ currentPriceOption.precio4MinPrice }})</span
                      >
                    </span>
                  </button>
                  <button
                    v-if="userCanSeePrecio5"
                    type="button"
                    @click="editModalItem.price = currentPriceOption.precio5"
                    class="price-option-btn"
                    :class="{
                      'is-selected': transformedPriceLevel === 'precio5'
                    }"
                  >
                    <span class="price-label">GrupoNKR</span>
                    <span class="price-amount">
                      {{ currency }}{{ currentPriceOption.precio5 }}
                      <span
                        v-if="currentPriceOption.precio5MinPrice"
                        class="price-min"
                        >({{ currency
                        }}{{ currentPriceOption.precio5MinPrice }})</span
                      >
                    </span>
                  </button>
                  <button
                    v-if="userCanSeePrecio6"
                    type="button"
                    @click="editModalItem.price = currentPriceOption.precio6"
                    class="price-option-btn"
                    :class="{
                      'is-selected': transformedPriceLevel === 'precio6'
                    }"
                  >
                    <span class="price-label">WH2</span>
                    <span class="price-amount">
                      {{ currency }}{{ currentPriceOption.precio6 }}
                      <span
                        v-if="currentPriceOption.precio6MinPrice"
                        class="price-min"
                        >({{ currency
                        }}{{ currentPriceOption.precio6MinPrice }})</span
                      >
                    </span>
                  </button>
                </div>
                <div class="mt-1 text-xs text-gray-500">
                  {{ $t("products_box.current_stock") }}:
                  {{ currentPriceOption.stock || 0 }}
                </div>
              </div>
            </div>
          </div>

          <!-- Cantidad y total en una fila -->
          <div class="flex items-end gap-3">
            <!-- Quantity -->
            <div id="quantity-control" class="shrink-0">
              <label class="block text-xs font-medium text-gray-700 mb-1">
                <i class="mdi mdi-package-variant mr-1"></i>
                {{ $t("products_box.quantity") }}
              </label>
              <div class="flex items-center gap-2">
                <button
                  @click="
                    editModalItem.quantity = Math.max(
                      1,
                      editModalItem.quantity - 1
                    )
                  "
                  class="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg text-lg font-bold"
                >
                  −
                </button>
                <input
                  v-model.number="editModalItem.quantity"
                  type="number"
                  min="1"
                  class="w-20 h-10 px-2 text-base text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  @click="editModalItem.quantity++"
                  class="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg text-lg font-bold"
                >
                  +
                </button>
              </div>
            </div>

            <!-- Total Preview -->
            <div
              id="total-preview"
              class="flex-1 h-10 flex items-center bg-blue-50 px-4 rounded-lg"
            >
              <div class="flex-1 flex justify-between items-center">
                <span class="text-gray-600"
                  >{{ $t("products_box.total") }}:</span
                >
                <span class="text-lg font-bold text-blue-600">
                  {{
                    (
                      (editModalItem.price || 0) * (editModalItem.quantity || 0)
                    ).toFixed(2) | currencyFormatted(true)
                  }}
                </span>
              </div>
            </div>
          </div>

          <!-- Kit Items Preview (for kits only) -->
          <div
            v-if="editModalType === 'kit' && editModalItem.kitItems"
            id="kit-items-preview"
            class="bg-gray-100 p-4 rounded-lg"
          >
            <h4 class="text-sm font-medium text-gray-700 mb-2">
              <i class="mdi mdi-package-variant-closed mr-1"></i>
              {{ $t("products_box.kit_contains") }}
            </h4>
            <div class="space-y-2">
              <div
                v-for="(kitItem, index) in editModalItem.kitItems"
                :key="index"
                class="flex justify-between text-sm text-gray-600 bg-white p-2 rounded"
              >
                <span>{{ kitItem.nkrCode }}</span>
                <span class="text-gray-500"
                  >{{ $t("products_box.qty") }}: {{ kitItem.quantity }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div
          id="edit-modal-footer"
          class="px-4 py-2 border-t border-gray-200 bg-white"
        >
          <div class="flex gap-3">
            <button
              @click="cancelEditModal"
              class="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
            >
              {{ $t("products_box.cancel") }}
            </button>
            <button
              @click="saveEditModal"
              class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
            >
              <i class="mdi mdi-check mr-1"></i>
              {{ $t("products_box.save_changes") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import NewProductLine from "@/components/order/NewProductLine.vue";
import { updateOrder } from "@/api/store/OrdersService.js";
import { getProducts } from "@/api/store/ProductsService.js";
import { selectBestLine } from "@/utils/product-line.js";
import { camelCase } from "lodash";

export default {
  name: "ProductsBox",
  components: {
    NewProductLine
  },
  props: {
    products: {
      type: Array,
      required: true
    },
    kitProducts: {
      type: Array,
      required: true
    },
    editing: {
      type: String,
      required: true
    },
    priceLevel: {
      type: String,
      required: true
    },
    orderNumber: {
      type: String,
      required: true
    },
    productsQty: {
      type: Number,
      required: true
    },
    productsLines: {
      type: Number,
      required: true
    },
    canBeEdited: {
      type: Boolean,
      required: true
    },
    orderDepartmentCode: {
      type: String,
      required: true
    },
    // cupón de la orden: su descuento por línea viene en item.couponDiscount
    couponCode: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      imagePreview: null,
      edit: false,
      editingProducts: [],
      editingProductsKits: [],
      newProduct: null,
      renderId: 0,
      loading: false,
      showShoppingCartItems: false,
      // Mobile edit modal
      showEditModal: false,
      showChangesPreview: false,
      editModalItem: null,
      editModalType: "product", // 'product' or 'kit'
      editModalOriginalParent: null,
      editModalOriginalNextSibling: null,
      editModalBackup: null,
      nkrCodeFilter: "",
      sortColumn: "order",
      sortDirection: "asc",
      lastAddedItem: null
    };
  },
  mounted() {
    this.editingProducts = this.products.map(p => ({ ...p }));
    this.editingProductsKits = this.kitProducts.map(k => ({ ...k }));
  },
  beforeDestroy() {
    clearTimeout(this.lastAddedTimer);
  },
  computed: {
    // columna Descuento: sólo si algún producto tiene descuento del cupón
    hasCouponLines() {
      return this.products.some(item => this.couponDiscount(item) > 0);
    },
    visibleCouponDiscount() {
      return this.filteredAndSortedProducts.reduce(
        (sum, item) => sum + this.couponDiscount(item),
        0
      );
    },
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    userCanSeePrecio4() {
      return this.$store.getters["auth/userHasPermission"]("price_precio4");
    },
    userCanSeePrecio5() {
      return this.$store.getters["auth/userHasPermission"]("price_precio5");
    },
    userCanSeePrecio6() {
      return this.$store.getters["auth/userHasPermission"]("price_precio6");
    },
    transformedPriceLevel() {
      const upFirst = word =>
        word[0].toUpperCase() + word.toLowerCase().slice(1);
      const camelize = text => {
        let words = text.split(/[-_]/g);
        return (
          words[0].toLowerCase() +
          words
            .slice(1)
            .map(upFirst)
            .join("")
        );
      };
      return camelize(this.priceLevel);
    },
    currentPriceOption() {
      if (!this.editModalItem || this.editModalType !== "product") return null;
      // El mismo productId puede venir de varios departamentos: se busca
      // primero por su deposito
      const lines = this.editModalItem.pricesAndStock || [];
      return (
        lines.find(line => this.isEditModalLine(line)) ||
        lines.find(line => line.productId == this.editModalItem.productId)
      );
    },
    // Lineas del producto del modal en el departamento de la orden. Se puede
    // cambiar a las vendibles que no esten ya en ella (la orden guarda una
    // fila por productId); el resto se muestra deshabilitado con el motivo.
    // Los custom y los transfers no tienen lineas a las que cambiar. Las
    // filas guardadas traen isTransfer como "0"/"1" (string)
    editModalCanChangeLine() {
      const item = this.editModalItem;
      return (
        this.editModalType === "product" &&
        !!item &&
        !item.custom &&
        !(item.isTransfer === true || item.isTransfer == 1)
      );
    },
    editModalLineOptions() {
      if (!this.editModalCanChangeLine) return [];
      const item = this.editModalItem;
      const taken = new Set(
        this.editingProducts
          .filter(product => product !== item)
          .map(product => String(product.productId))
      );
      const blockedReason = line => {
        if (this.isEditModalLine(line)) return null;
        if (taken.has(String(line.productId))) return "in_order";
        if (line.canSell !== true) return "no_stock";
        return null;
      };
      return (
        (item.pricesAndStock || [])
          // Mismo productId en otro deposito no se puede guardar: la orden
          // guarda una fila por productId y al actualizar no cambia el deposito
          .filter(
            line =>
              this.isEditModalLine(line) ||
              (line.departamentoCodigo === this.orderDepartmentCode &&
                line.productId != item.productId)
          )
          .map(line => ({ ...line, blocked: blockedReason(line) }))
      );
    },
    productOrderMap() {
      if (!Array.isArray(this.editingProducts)) return new Map();
      const sorted = [...this.editingProducts].sort(
        (a, b) => Number(a.productId) - Number(b.productId)
      );
      const map = new Map();
      sorted.forEach((item, index) => {
        map.set(item.productId, index + 1);
      });
      return map;
    },
    filteredAndSortedProducts() {
      if (!Array.isArray(this.editingProducts)) return [];

      let products = [...this.editingProducts];
      const normalizedFilter = this.nkrCodeFilter.toLowerCase().trim();

      if (normalizedFilter) {
        products = products.filter(item => {
          return this.getProductCode(item)
            .toLowerCase()
            .includes(normalizedFilter);
        });
      }

      return products.sort((a, b) => {
        const aValue = this.getSortValue(a, this.sortColumn);
        const bValue = this.getSortValue(b, this.sortColumn);
        let compareResult = 0;

        if (typeof aValue === "number" && typeof bValue === "number") {
          compareResult = aValue - bValue;
        } else {
          compareResult = `${aValue}`.localeCompare(`${bValue}`, undefined, {
            numeric: true,
            sensitivity: "base"
          });
        }

        return this.sortDirection === "asc" ? compareResult : -compareResult;
      });
    },
    visibleProductsQty() {
      return this.filteredAndSortedProducts.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
      );
    },
    visibleProductsTotal() {
      return this.filteredAndSortedProducts.reduce(
        (sum, item) =>
          sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
        0
      );
    },
    // Contadores en vivo mientras se edita (las props son lo guardado)
    liveLines() {
      return (
        this.editingProducts.length +
        this.editingProductsKits.reduce(
          (sum, kit) => sum + ((kit.kitItems || []).length || 1),
          0
        )
      );
    },
    liveQty() {
      const productsQty = this.editingProducts.reduce(
        (sum, item) => sum + (Number(item.quantity) || 0),
        0
      );
      const kitsQty = this.editingProductsKits.reduce((sum, kit) => {
        const items = kit.kitItems || [];
        return items.length
          ? sum +
              items.reduce((acc, item) => acc + (Number(item.quantity) || 0), 0)
          : sum + (Number(kit.quantity) || 0);
      }, 0);
      return productsQty + kitsQty;
    },
    liveTotal() {
      const productsTotal = this.editingProducts.reduce(
        (sum, item) =>
          sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
        0
      );
      const kitsTotal = this.editingProductsKits.reduce(
        (sum, kit) =>
          sum + this.kitUnitPrice(kit) * (Number(kit.quantity) || 0),
        0
      );
      return productsTotal + kitsTotal;
    },
    // What saving will change, line by line (from buildOrderUpdates), for the
    // preview before saving. renderId: same reason as pendingChangesCount
    changesPreview() {
      if (!this.edit || this.renderId < 0) return null;
      const updates = this.buildOrderUpdates();
      const productLine = (item, kind, before) => {
        const shown = {
          qty: Number(item.quantity) || 0,
          price: Number(item.price) || 0
        };
        return {
          key: `${kind}-${item.productId}-${item.warehouseId}`,
          code: item.nkrCode || item.sku,
          description: item.description,
          isKit: false,
          before,
          shown,
          qtyChanged: !!before && before.qty !== shown.qty,
          priceChanged: !!before && before.price !== shown.price
        };
      };
      const kitLine = (kit, kind, before) => {
        const shown = {
          qty: Number(kit.quantity) || 0,
          price: this.kitUnitPrice(kit)
        };
        return {
          key: `${kind}-kit-${kit.kitCode}`,
          code: kit.kitCode,
          description: kit.description,
          isKit: true,
          before,
          shown,
          qtyChanged: !!before && before.qty !== shown.qty,
          priceChanged: false
        };
      };
      const productBefore = item => {
        const original = this.products.find(p => p.productId == item.productId);
        return {
          qty: Number(original.quantity) || 0,
          price: Number(original.price) || 0
        };
      };
      const kitBefore = kit => {
        const original = this.kitProducts.find(k => k.kitCode == kit.kitCode);
        return {
          qty: Number(original.quantity) || 0,
          price: this.kitUnitPrice(original)
        };
      };
      const groups = [
        {
          kind: "added",
          icon: "mdi-plus-circle-outline",
          label: "products_box.changes_added",
          lines: [
            ...updates.productsToAdd.map(p => productLine(p, "added", null)),
            ...updates.kitProductsToAdd.map(k => kitLine(k, "added", null))
          ]
        },
        {
          kind: "updated",
          icon: "mdi-pencil-circle-outline",
          label: "products_box.changes_updated",
          lines: [
            ...updates.productsToUpdate.map(p =>
              productLine(p, "updated", productBefore(p))
            ),
            ...updates.kitProductsToUpdate.map(k =>
              kitLine(k, "updated", kitBefore(k))
            )
          ]
        },
        {
          kind: "removed",
          icon: "mdi-minus-circle-outline",
          label: "products_box.changes_removed",
          lines: [
            ...updates.productsToDelete.map(p =>
              productLine(p, "removed", null)
            ),
            ...updates.kitProductsToDelete.map(k => kitLine(k, "removed", null))
          ]
        }
      ].filter(group => group.lines.length);
      const totalBefore =
        this.products.reduce(
          (sum, p) => sum + (Number(p.price) || 0) * (Number(p.quantity) || 0),
          0
        ) +
        this.kitProducts.reduce(
          (sum, k) => sum + this.kitUnitPrice(k) * (Number(k.quantity) || 0),
          0
        );
      return {
        groups,
        totalBefore,
        diff: this.liveTotal - totalBefore
      };
    },
    pendingChangesCount() {
      // renderId: updateProduct reemplaza filas sin reactividad y hay que
      // recalcular igual
      if (!this.edit || this.renderId < 0) return 0;
      return Object.values(this.buildOrderUpdates()).reduce(
        (sum, changes) => sum + changes.length,
        0
      );
    }
  },
  methods: {
    // descuento del cupón en la línea (orders_v2_products.coupon_discount)
    couponDiscount(item) {
      return Number(item.couponDiscount) || 0;
    },
    couponLineTotal(item) {
      return Math.max(
        0,
        item.price * item.quantity - this.couponDiscount(item)
      );
    },
    openImagePreview(item) {
      this.imagePreview = {
        src: item.image,
        code: this.getProductCode(item),
        description: item.description || ""
      };
      // Foco en el overlay para que Esc lo cierre sin cerrar el modal de orden
      this.$nextTick(() => this.$refs.imagePreview?.focus());
    },
    getProductCode(item) {
      return item.nkrCode || item.sku || "";
    },
    hasStockInOtherWarehouses(item) {
      if (
        !item.pricesAndStockOtherWarehouses ||
        !Array.isArray(item.pricesAndStockOtherWarehouses)
      ) {
        return false;
      }
      return item.pricesAndStockOtherWarehouses.some(
        warehouse => warehouse.stock > 0
      );
    },
    getOtherWarehousesWithStock(item) {
      if (
        !item.pricesAndStockOtherWarehouses ||
        !Array.isArray(item.pricesAndStockOtherWarehouses)
      ) {
        return [];
      }
      return item.pricesAndStockOtherWarehouses
        .filter(warehouse => warehouse.stock > 0)
        .map(warehouse => ({
          code: warehouse.warehouse,
          stock: warehouse.stock,
          name: warehouse.departamento_nombre
        }));
    },
    formatOtherWarehouseStockLabel(warehouse, index, item) {
      const warehouses = this.getOtherWarehousesWithStock(item);
      const separator = index < warehouses.length - 1 ? ", " : "";
      return `${warehouse.code} (${warehouse.stock})${separator}`;
    },
    getSortValue(item, column) {
      switch (column) {
        case "order":
          return this.productOrderMap.get(item.productId) || 0;
        case "code":
          return this.getProductCode(item);
        case "description":
          return item.description || "";
        case "line":
          return item.line || "";
        case "brand":
          return item.brand || "";
        case "price":
          return Number(item.price) || 0;
        case "quantity":
          return Number(item.quantity) || 0;
        case "total":
          return (Number(item.price) || 0) * (Number(item.quantity) || 0);
        default:
          return this.productOrderMap.get(item.productId) || 0;
      }
    },
    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        return;
      }
      this.sortColumn = column;
      this.sortDirection = "asc";
    },
    getSortIcon(column) {
      if (this.sortColumn !== column) {
        return "mdi mdi-unfold-more-horizontal text-gray-400";
      }
      return this.sortDirection === "asc"
        ? "mdi mdi-chevron-up"
        : "mdi mdi-chevron-down";
    },
    startEdit() {
      this.edit = true;
      this.$emit("startEditing", "productsBox");
      // Scroll to show the products box
      this.$nextTick(() => {
        this.$el.scrollIntoView({
          behavior: "smooth",
          block: "start"
        });
      });
    },
    revertChanges() {
      this.edit = false;
      this.editingProducts = this.products.map(p => ({ ...p }));
      this.editingProductsKits = this.kitProducts.map(k => ({ ...k }));
      this.lastAddedItem = null;
      this.$emit("finishedEditing", false);
      this.renderId++;
    },
    addProduct(newProduct) {
      this.editingProducts.push(newProduct);
      this.addCoreFor(newProduct);
      this.renderId++;
      // Resalta la fila nueva un momento y la lleva a la vista (tabla en
      // escritorio, tarjeta en movil: solo una de las dos es visible)
      this.lastAddedItem = newProduct;
      clearTimeout(this.lastAddedTimer);
      this.lastAddedTimer = setTimeout(() => {
        this.lastAddedItem = null;
      }, 2500);
      this.$nextTick(() => {
        const row = [...this.$el.querySelectorAll(".is-new")].find(
          element => element.offsetParent !== null
        );
        if (row) {
          row.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      });
    },
    // Una pieza con core (p. ej. 330-4283) arrastra su core, que en Infoauto
    // es un producto aparte (330-4283-CO), como linea propia y con la misma
    // cantidad, justo debajo de la pieza.
    async addCoreFor(product) {
      const coreCode = product.coreNkrCode;
      if (!coreCode || this.findLine(coreCode)) return;
      try {
        const res = await getProducts({
          storeEnabled: "null",
          page: 1,
          limitPerPage: "10",
          orderCol: "nkr_code",
          orderDir: "asc",
          search: coreCode,
          requestPrice: true,
          priceLevel: "online",
          departmentCode: this.orderDepartmentCode
        });
        const core = res.products.find(p => p.nkrCode === coreCode);
        const lines = ((core && core.pricesAndStock) || []).filter(
          line =>
            line.canSell === true &&
            line.departamentoCodigo === this.orderDepartmentCode &&
            line.warehouse !== "FBA" &&
            line.line !== "OB"
        );
        if (lines.length === 0) throw new Error(`${coreCode} not sellable`);
        // Mientras cargaba pudieron quitar la pieza o agregar el core a mano
        const index = this.editingProducts.indexOf(product);
        if (index === -1 || this.findLine(coreCode)) return;
        const priceKey = camelCase(this.priceLevel);
        const best = selectBestLine(lines, priceKey);
        this.editingProducts.splice(index + 1, 0, {
          productId: best.productId,
          nkrCode: core.nkrCode,
          line: best.line,
          description: core.customTitle || core.originalTitle,
          brand: core.brand,
          image: core.image,
          pack: core.pack,
          price: best[priceKey],
          quantity: product.quantity,
          warehouseId: best.warehouseId,
          warehouseCode: best.warehouse,
          departmentCode: best.departamentoCodigo,
          unificado: core.unificado || null,
          custom: false,
          isTransfer: false,
          pricesAndStock: core.pricesAndStock,
          coreOf: product.nkrCode
        });
        this.renderId++;
        this.$toast.open({
          message: this.$t("products_box.core_added", { code: coreCode }),
          type: "info",
          position: "top-right"
        });
      } catch (e) {
        console.error("Failed to add core", e);
        this.$toast.open({
          message: this.$t("products_box.core_not_added", { code: coreCode }),
          type: "warning",
          position: "top-right"
        });
      }
    },
    findLine(nkrCode) {
      return this.editingProducts.find(p => p.nkrCode === nkrCode);
    },
    // El core acompana la cantidad de su pieza
    syncCoreQty(item) {
      if (!item.coreNkrCode) return;
      const core = this.findLine(item.coreNkrCode);
      if (core && core.coreOf === item.nkrCode) {
        this.$set(core, "quantity", item.quantity);
      }
    },
    addKit(newKit) {
      this.editingProductsKits.push(newKit);
      this.renderId++;
    },
    scrollToBottom() {
      // Scroll to show the add product section
      this.$nextTick(() => {
        const addProductSection = this.$el.querySelector(
          "#add-product-section"
        );
        if (addProductSection) {
          addProductSection.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
        }
      });
    },
    // Diferencias entre lo guardado y lo que se esta editando. Lo usan
    // saveChanges y el contador de cambios pendientes de la barra inferior.
    buildOrderUpdates() {
      const productsToDelete = this.products.filter(product => {
        return !this.editingProducts.some(
          editingProduct => editingProduct.productId == product.productId
        );
      });
      const productsToAdd = this.editingProducts.filter(editingProduct => {
        return !this.products.some(
          product => product.productId == editingProduct.productId
        );
      });
      const productsToUpdate = this.editingProducts.filter(editingProduct => {
        return this.products.find(product => {
          return (
            product.productId == editingProduct.productId &&
            (editingProduct.quantity != product.quantity ||
              editingProduct.price != product.price)
          );
        });
      });
      const kitProductsToDelete = this.kitProducts.filter(product => {
        return !this.editingProductsKits.some(
          editingProduct => editingProduct.kitCode == product.kitCode
        );
      });
      const kitProductsToAdd = this.editingProductsKits.filter(
        editingProduct => {
          return !this.kitProducts.some(
            product => product.kitCode == editingProduct.kitCode
          );
        }
      );
      const kitProductsToUpdate = this.editingProductsKits.filter(
        editingProduct => {
          return this.kitProducts.find(product => {
            return (
              product.kitCode == editingProduct.kitCode &&
              editingProduct.quantity !== product.quantity
            );
          });
        }
      );
      return {
        productsToDelete,
        productsToAdd,
        productsToUpdate,
        kitProductsToDelete,
        kitProductsToUpdate,
        kitProductsToAdd
      };
    },
    openAddProduct() {
      if (this.$refs.newProductLine) {
        this.$refs.newProductLine.openSearchModal();
      }
    },
    kitUnitPrice(kit) {
      // Los kits guardados traen kitPrice; los recien agregados, price
      return Number(kit.price ?? kit.kitPrice) || 0;
    },
    saveChanges() {
      if (!this.loading) {
        this.loading = true;
        const orderUpdates = this.buildOrderUpdates();
        if (
          Object.values(orderUpdates).every(changes => changes.length === 0)
        ) {
          this.revertChanges();
          this.loading = false;
          return;
        }
        updateOrder(this.orderNumber, "products-update", orderUpdates)
          .then(res => {
            this.$toast.open({
              message: "Order Updated",
              type: "success",
              position: "top-right"
            });

            if (res.warnings && res.warnings.length > 0) {
              this.$toast.open({
                message: `Order updated with warnings: ${res.warnings.join(
                  ", "
                )}`,
                type: "warning",
                position: "top-right",
                duration: 10000
              });
            }
            this.$emit("finishedEditing", true);
          })
          .catch(e => {
            this.$toast.open({
              message: e.response.data.message ?? "Error updating order",
              type: "error",
              position: "top-right"
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    // Quantity changes never bump renderId: it is in the desktop row key, so
    // it recreated every row; the input lost focus mid-typing and in long
    // orders the view jumped to the end. $set is enough to re-render
    incrementQty(item) {
      const next = (Number(item.quantity) || 0) + 1;
      this.$set(item, "quantity", next);
      this.syncCoreQty(item);
    },
    decrementQty(item) {
      const next = Math.max(1, (Number(item.quantity) || 1) - 1);
      this.$set(item, "quantity", next);
      this.syncCoreQty(item);
    },
    // Per keystroke: an empty or invalid value is left alone so the field can
    // be cleared and retyped
    setQty(item, value) {
      const parsed = parseInt(value, 10);
      if (isNaN(parsed) || parsed < 1) return;
      this.$set(item, "quantity", parsed);
      this.syncCoreQty(item);
    },
    // On leaving the field: what was left empty or invalid goes back to 1
    commitQty(item, event) {
      const parsed = parseInt(event.target.value, 10);
      const next = isNaN(parsed) || parsed < 1 ? 1 : parsed;
      this.$set(item, "quantity", next);
      this.syncCoreQty(item);
      event.target.value = next;
    },
    removeItem(code) {
      this.editingProducts = this.editingProducts.filter(product => {
        return product.nkrCode != code;
      });
      this.renderId++;
    },
    removeItemById(productId) {
      const removed = this.editingProducts.find(
        product => product.productId == productId
      );
      this.editingProducts = this.editingProducts.filter(product => {
        return (
          product.productId != productId &&
          !(removed && removed.nkrCode && product.coreOf === removed.nkrCode)
        );
      });
      this.renderId++;
    },
    removeKit(kitCode) {
      this.editingProductsKits = this.editingProductsKits.filter(product => {
        return product.kitCode != kitCode;
      });
      this.renderId++;
    },
    updateProduct(newProduct) {
      const index = this.editingProducts.findIndex(
        product => product.productId == newProduct.productId
      );
      this.editingProducts[index] = newProduct;
      // force rerender lines :S
      this.renderId++;
    },
    updateKitProduct(newProduct) {
      const index = this.editingProductsKits.findIndex(
        product => product.kitCode == newProduct.kitCode
      );
      this.editingProductsKits[index] = newProduct;
      // force rerender lines :S
      this.renderId++;
    },
    // Mobile Edit Modal Methods
    openEditModal(item, type) {
      // Keep a backup for cancel, but bind directly so changes are live in the table
      this.editModalBackup = JSON.parse(JSON.stringify(item));
      this.editModalItem = item;
      this.editModalType = type;
      this.showEditModal = true;

      // Lock body scroll and move modal to body for iOS Safari
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;

      this.$nextTick(() => {
        const modal = this.$refs.editModal;
        if (modal) {
          this.editModalOriginalParent = modal.parentNode;
          this.editModalOriginalNextSibling = modal.nextSibling;
          document.body.appendChild(modal);
        }
      });
    },
    closeEditModal() {
      // Move modal back to original position
      const modal = this.$refs.editModal;
      if (modal && this.editModalOriginalParent) {
        if (this.editModalOriginalNextSibling) {
          this.editModalOriginalParent.insertBefore(
            modal,
            this.editModalOriginalNextSibling
          );
        } else {
          this.editModalOriginalParent.appendChild(modal);
        }
      }

      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);

      this.showEditModal = false;
      this.editModalItem = null;
      this.editModalOriginalParent = null;
      this.editModalOriginalNextSibling = null;
    },
    isEditModalLine(line) {
      return (
        line.productId == this.editModalItem.productId &&
        line.warehouseId == this.editModalItem.warehouseId
      );
    },
    // Cambiar de linea es cambiar de productId: al guardar sale como quitar
    // la vieja y agregar la nueva, con el precio de la nueva
    selectEditModalLine(line) {
      const changes = {
        productId: line.productId,
        line: line.line,
        warehouseId: line.warehouseId,
        warehouseCode: line.warehouse,
        departmentCode: line.departamentoCodigo,
        coreNkrCode: line.coreNkrCode || null,
        price: line[this.transformedPriceLevel],
        stockOnConfirmation: Number(line.stock) || 0
      };
      // $set: las filas guardadas no traen todas estas claves
      Object.entries(changes).forEach(([key, value]) =>
        this.$set(this.editModalItem, key, value)
      );
    },
    cancelEditModal() {
      // Restore original values since editModalItem is a direct reference
      if (this.editModalBackup && this.editModalItem) {
        Object.assign(this.editModalItem, this.editModalBackup);
        this.renderId++;
      }
      this.editModalBackup = null;
      this.closeEditModal();
    },
    saveEditModal() {
      // editModalItem is a direct reference so changes are already in editingProducts
      if (this.editModalType === "product")
        this.syncCoreQty(this.editModalItem);
      this.editModalBackup = null;
      this.renderId++;
      this.closeEditModal();
    }
  }
};
</script>
<style scoped>
/* Relacion pieza <-> core */
.core-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
}
.core-link.is-core {
  color: #475569;
  background: #f1f5f9;
  border-color: #cbd5e1;
}
tr.is-core-row td:first-child,
.product-row.is-core-row {
  box-shadow: inset 3px 0 0 #fcd34d;
}

/* Product box should grow with content on mobile/tablet */
.product-box {
  overflow: visible;
}

/* Cabecera */
.products-header {
  flex-wrap: wrap;
  row-gap: 8px;
}

.products-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
  min-width: 0;
}

.products-title .title {
  margin: 0 4px 0 0;
}

.stat-chip,
.editing-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}

.stat-chip {
  background: #f3f4f6;
  color: #4b5563;
}

.editing-chip {
  background: #fef3c7;
  color: #92400e;
}

.products-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.pb-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  font-size: 16px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, filter 0.15s;
}

.pb-btn i {
  font-size: 18px;
}

.pb-btn:hover:not(:disabled) {
  border-color: #9ca3af;
  background: #f9fafb;
}

.pb-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pb-btn.is-primary,
.pb-btn.is-success {
  color: white;
}

.pb-btn.is-primary {
  border-color: #126e6a;
  background: #126e6a;
}

.pb-btn.is-success {
  border-color: #16a34a;
  background: #16a34a;
}

.pb-btn.is-primary:hover:not(:disabled),
.pb-btn.is-success:hover:not(:disabled) {
  filter: brightness(1.08);
}

.pb-btn.is-primary:hover:not(:disabled) {
  border-color: #126e6a;
  background: #126e6a;
}

.pb-btn.is-success:hover:not(:disabled) {
  border-color: #16a34a;
  background: #16a34a;
}

/* Barra de guardado: sticky abajo mientras se edita (en movil, fixed) */
.edit-action-bar {
  position: sticky;
  bottom: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 16px;
  margin-top: 12px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 -4px 16px rgba(16, 24, 40, 0.08);
}

.edit-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  font-size: 16px;
  color: #4b5563;
}

.changes-status {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-weight: 500;
  color: #6b7280;
}

.changes-status i {
  font-size: 18px;
}

.changes-status.has-changes {
  color: #b45309;
}

.changes-status.has-changes i {
  font-size: 22px;
}

.summary-totals strong {
  color: #111827;
  font-variant-numeric: tabular-nums;
}

.edit-buttons {
  display: flex;
  gap: 8px;
  margin-left: auto;
}

.edit-buttons .label-short {
  display: none;
}

@media (max-width: 767px) {
  .edit-action-bar {
    position: fixed;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 45;
    margin: 0;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    border-radius: 0;
  }

  .edit-buttons {
    width: 100%;
  }

  /* Con "Descartar Cambios" y "Guardar Cambios" (nowrap) no cabian en 390px
     y el segundo se salia por la derecha: textos cortos, y min-width 0 para
     que encojan si aun asi no caben */
  .edit-buttons .pb-btn {
    flex: 1;
    min-width: 0;
    height: 44px;
    overflow: hidden;
  }

  .edit-buttons .label-long {
    display: none;
  }

  .edit-buttons .label-short {
    display: inline;
  }

  .product-box.is-editing {
    margin-bottom: 110px;
  }
}

/* Estado con cambios: abre el resumen */
.changes-status.is-button {
  gap: 4px;
  padding: 2px 6px;
  margin: -2px -6px;
  border-radius: 6px;
  background: none;
  font-size: inherit;
  cursor: pointer;
}

.changes-status.is-button:hover {
  background: #fef3c7;
}

.view-changes {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 4px;
  color: #126e6a;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* Resumen de cambios */
.changes-preview {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
}

.changes-group-title {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.changes-group.is-added .changes-group-title {
  color: #15803d;
}

.changes-group.is-updated .changes-group-title {
  color: #b45309;
}

.changes-group.is-removed .changes-group-title {
  color: #b91c1c;
}

.changes-group-count {
  min-width: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #374151;
  font-size: 13px;
  text-align: center;
}

.change-line {
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-left-width: 3px;
  border-radius: 8px;
}

.change-line + .change-line {
  margin-top: 6px;
}

.is-added .change-line {
  border-left-color: #16a34a;
  background: #f0fdf4;
}

.is-updated .change-line {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.is-removed .change-line {
  border-left-color: #dc2626;
  background: #fef2f2;
}

.is-removed .change-line-main,
.is-removed .change-line-detail {
  text-decoration: line-through;
  color: #6b7280;
}

.change-line-main {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.change-code {
  flex-shrink: 0;
  font-weight: 700;
  color: #111827;
}

.change-kit {
  flex-shrink: 0;
  padding: 0 6px;
  border-radius: 4px;
  background: #ede9fe;
  color: #6d28d9;
  font-size: 12px;
  font-weight: 700;
}

.change-description {
  min-width: 0;
  overflow: hidden;
  color: #4b5563;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.change-line-detail {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 14px;
  margin-top: 4px;
  font-size: 15px;
  color: #4b5563;
}

.change-line-detail .is-changed {
  color: #92400e;
}

.change-line-detail s {
  color: #9ca3af;
}

.change-subtotal {
  margin-left: auto;
  font-weight: 600;
  color: #111827;
}

.changes-totals {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f9fafb;
}

.changes-totals > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.changes-diff {
  margin-left: 6px;
  font-style: normal;
  font-size: 14px;
}

.changes-diff.is-up {
  color: #15803d;
}

.changes-diff.is-down {
  color: #b91c1c;
}

.changes-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

@media (max-width: 767px) {
  .changes-actions {
    position: sticky;
    bottom: 0;
    padding: 10px 0 calc(10px + env(safe-area-inset-bottom));
    background: white;
  }

  .changes-actions .pb-btn {
    flex: 1;
    height: 44px;
  }
}

/* Fila recien agregada */
.is-new,
.is-new td {
  animation: row-added 2.5s ease-out;
}

@keyframes row-added {
  0%,
  40% {
    background-color: #dcfce7;
  }
  100% {
    background-color: transparent;
  }
}

.add-product-section {
  margin-top: 0.75rem;
}

.filter-count {
  font-size: 1rem;
  color: #6b7280;
}

/* Table wrapper */
.table-full {
  max-height: none;
  overflow: visible;
}

/* Desktop: scroll interno solo en modo lectura. Editando, la tabla crece y la
   barra de guardado queda fija abajo (antes solo cabian ~4 filas en 60vh) */
@media (min-width: 1280px) {
  .product-box:not(.is-editing) .table-full {
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-gutter: stable;
  }
}

/* Products List Container */
.products-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-products-list {
  display: flex;
}

/* Mobile Products Total */
.products-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  margin-top: 0.25rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
}

.products-total-label {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
}

.products-total-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: #059669;
}

.desktop-products-tools,
.desktop-products-table {
  display: none;
}

.desktop-filter-label {
  font-size: 1rem;
  color: #6b7280;
  font-weight: 600;
  display: block;
  margin-bottom: 0.25rem;
}

.desktop-filter-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.desktop-filter-input-wrap .filter-icon {
  position: absolute;
  left: 0.5rem;
  font-size: 1.125rem;
  color: #6b7280;
  pointer-events: none;
}

.desktop-filter-input {
  width: 240px;
  height: 34px;
  padding: 0 2rem 0 2rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
}

.desktop-filter-input:focus {
  outline: none;
  border-color: #126e6a;
  box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.12);
}

.desktop-filter-clear {
  position: absolute;
  right: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  color: #6b7280;
}

.desktop-filter-clear:hover {
  background: #f3f4f6;
  color: #111827;
}

/* Product Row - Desktop: horizontal layout */
.product-row {
  display: grid;
  grid-template-columns: 60px 1fr auto auto;
  gap: 1rem;
  padding: 0.75rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  align-items: center;
  transition: box-shadow 0.2s;
}

.product-row:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.kit-row {
  border-left: 3px solid #8b5cf6;
}

/* Product Image */
.product-image {
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Product Info */
.product-info {
  min-width: 0;
  flex: 1;
}

.product-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  flex-wrap: wrap;
}

.product-sku {
  font-weight: 600;
  font-size: 1rem;
  color: #1f2937;
}

.product-brand {
  font-size: 1rem;
  color: #6b7280;
  background: #f3f4f6;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
}

.product-description {
  font-size: 1rem;
  color: #4b5563;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-line {
  font-size: 1rem;
  margin-top: 0.25rem;
}

/* Kit Badge */
.kit-badge {
  background: #ede9fe;
  color: #7c3aed;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
}

/* Kit Items Preview */
.kit-items-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  margin-top: 0.375rem;
}

.kit-items-label {
  font-size: 1rem;
  color: #6b7280;
}

.kit-item-tag {
  font-size: 1rem;
  background: #dbeafe;
  color: #1e40af;
  padding: 0.125rem 0.375rem;
  border-radius: 3px;
}

.kit-items-more {
  font-size: 1rem;
  color: #6b7280;
}

/* Product Pricing */
.product-pricing {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.price-item {
  text-align: center;
  min-width: 60px;
}

.price-label {
  display: block;
  font-size: 0.8125rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.price-value {
  display: block;
  font-weight: 600;
  font-size: 1rem;
  color: #1f2937;
}

.price-item.total .price-value {
  color: #059669;
}

/* Product Actions */
.product-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.15s;
  border: none;
  cursor: pointer;
}

.action-text {
  display: none;
}

.view-btn {
  background: #eff6ff;
  color: #2563eb;
}

.view-btn:hover {
  background: #dbeafe;
}

.edit-btn {
  background: #fef3c7;
  color: #d97706;
}

.edit-btn:hover {
  background: #fde68a;
}

.delete-btn {
  background: #fee2e2;
  color: #dc2626;
}

.delete-btn:hover {
  background: #fecaca;
}

/* Inline Qty Control */
.qty-inline-control {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  justify-content: flex-end;
}

.qty-btn {
  width: 1.75rem;
  height: 1.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f3f4f6;
  color: #1f2937;
  font-weight: 700;
  font-size: 1rem;
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: background 0.15s;
}

.qty-btn:hover {
  background: #e5e7eb;
}

.qty-inline-input {
  width: 3rem;
  text-align: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.25rem 0.25rem;
  font-size: 1rem;
  font-weight: 600;
}

.qty-inline-input:focus {
  outline: none;
  border-color: #126e6a;
}

/* Kits Section */
.kits-section {
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 2px solid #e5e7eb;
}

.kits-title {
  font-weight: 600;
  color: #7c3aed;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.kits-title::before {
  content: "";
  display: inline-block;
  width: 4px;
  height: 1rem;
  background: #7c3aed;
  border-radius: 2px;
}

/* Mobile Layout */
@media (max-width: 767px) {
  .product-row {
    grid-template-columns: 44px minmax(0, 1fr) auto;
    grid-template-rows: auto auto auto;
    gap: 0.375rem 0.5rem;
    padding: 0.5rem 0.625rem;
  }

  .product-image {
    width: 44px;
    height: 44px;
    grid-row: 1 / 2;
  }

  .product-info {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }

  .product-pricing {
    grid-column: 1 / -1;
    grid-row: 2 / 3;
    justify-content: space-between;
    padding: 0.375rem 0 0;
    border-top: 1px solid #f3f4f6;
  }

  .product-actions {
    grid-column: 1 / -1;
    grid-row: 3 / 4;
    justify-content: flex-end;
  }

  .action-text {
    display: inline;
  }

  /* Solo lectura: "ver" es el unico boton y ocupaba una fila entera por
     producto; sube a la esquina de la primera fila como icono */
  .product-box:not(.is-editing) .product-actions {
    grid-column: 3 / 4;
    grid-row: 1 / 2;
    align-self: start;
  }

  .product-box:not(.is-editing) .action-text {
    display: none;
  }

  .price-item {
    min-width: auto;
    flex: 1;
  }
}

/* Tablet Layout */
@media (min-width: 768px) and (max-width: 1023px) {
  .product-row {
    grid-template-columns: 60px 1fr auto auto;
  }

  .action-text {
    display: inline;
  }
}

/* Desktop Layout: tabla compacta para ver mas lineas a la vez */
@media (min-width: 1024px) {
  .desktop-products-tools {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-bottom: 0.625rem;
    gap: 0.75rem;
  }

  .desktop-products-table {
    display: block;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    overflow-x: auto;
  }

  .desktop-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 900px;
  }

  .desktop-table th,
  .desktop-table td {
    padding: 0.45rem 0.625rem;
    border-bottom: 1px solid #f3f4f6;
    text-align: left;
    vertical-align: middle;
    font-size: 1rem;
    line-height: 1.3;
    color: #374151;
  }

  .desktop-table th {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    background: #f9fafb;
    border-bottom-color: #e5e7eb;
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6b7280;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .desktop-table tbody tr {
    transition: background-color 0.1s;
  }

  .desktop-table tbody tr:hover {
    background-color: #f9fafb;
  }

  .col-num {
    width: 44px;
  }

  /* Incluye la miniatura del producto */
  .col-code {
    width: 160px;
  }

  .col-brand {
    width: 110px;
  }

  .col-line {
    width: 90px;
  }

  .col-price {
    width: 100px;
  }

  .col-qty {
    width: 124px;
  }

  .col-total {
    width: 110px;
  }

  .col-actions {
    width: 1%;
  }

  .desktop-total-row td {
    background: #f0fdf4;
    border-top: 1px solid #bbf7d0;
    border-bottom: none;
    font-size: 1rem;
  }

  .sortable-col {
    cursor: pointer;
    user-select: none;
  }

  .sortable-col:hover {
    color: #111827;
  }

  .sortable-col i {
    margin-left: 0.125rem;
    font-size: 1rem;
    vertical-align: middle;
  }

  .text-right {
    text-align: right !important;
  }

  .desktop-code-wrap {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .desktop-thumb {
    display: block;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    padding: 2px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: white;
    cursor: zoom-in;
    transition: border-color 0.15s, box-shadow 0.15s;
  }

  .desktop-thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .desktop-thumb:hover {
    border-color: #9ca3af;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  }

  .desktop-code {
    font-weight: 700;
    color: #111827;
    white-space: nowrap;
  }

  .desktop-brand,
  .desktop-stock-on-conf,
  .desktop-warehouse {
    font-size: 1rem;
    color: #6b7280;
  }

  .desktop-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.3;
    color: #111827;
  }

  .desktop-line {
    font-size: 1rem;
    font-weight: 600;
    color: #2563eb;
  }

  .desktop-stock-on-conf.danger,
  .out-of-stock-row .desktop-line {
    color: #b91c1c;
  }

  .desktop-transfer {
    margin-top: 0.125rem;
    display: inline-block;
    font-size: 1rem;
    background: #dbeafe;
    color: #1d4ed8;
    padding: 0 0.35rem;
    border-radius: 4px;
    font-weight: 600;
  }

  .out-of-stock-row {
    background-color: #fef2f2;
  }

  .desktop-actions {
    display: flex;
    gap: 0.25rem;
    justify-content: flex-end;
    align-items: center;
  }

  /* Acciones solo con icono (el texto queda en el title) */
  .desktop-actions .action-btn {
    width: 30px;
    height: 30px;
    padding: 0;
    font-size: 1rem;
  }

  .desktop-actions .action-text {
    display: none;
  }

  .desktop-empty-results {
    text-align: center !important;
    color: #6b7280 !important;
    padding: 2rem 1rem !important;
    font-size: 1rem !important;
  }

  .desktop-empty-icon {
    display: block;
    font-size: 2.25rem;
    margin-bottom: 0.5rem;
    opacity: 0.4;
  }

  .mobile-products-list {
    display: none;
  }

  /* Tarjetas de kits en escritorio */
  .product-sku {
    font-size: 1rem;
  }

  .product-brand {
    font-size: 1rem;
  }

  .product-description {
    font-size: 1rem;
    line-height: 1.35;
  }

  .product-line {
    font-size: 1rem;
  }

  .price-label {
    font-size: 1rem;
  }

  .price-value {
    font-size: 1rem;
  }

  .price-item.total .price-value {
    font-size: 1rem;
  }

  .action-text {
    display: inline;
  }

  .action-btn {
    padding: 0.375rem 0.75rem;
    font-size: 1rem;
  }
}

/* Custom scrollbar */
.table-full::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.table-full::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-full::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-full::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Mobile improvements */
@media (max-width: 1279px) {
  .content {
    padding: 0.5rem;
    overflow: visible;
  }

  input[type="number"] {
    font-size: 16px;
  }
}

/* Sticky button improvements */
.sticky {
  backdrop-filter: blur(10px);
  background-color: rgba(255, 255, 255, 0.95);
}

/* Loading animation */
.mdi-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Price Option Buttons */
.price-option-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  background: white;
  cursor: pointer;
  transition: all 0.15s ease;
}

.price-option-btn:hover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.price-option-btn.is-selected {
  border-color: #dc2626;
  border-width: 2px;
  background: #fef2f2;
}

.price-option-btn .price-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6b7280;
  letter-spacing: 0.025em;
}

.price-option-btn .price-amount {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
}

.price-option-btn .price-min {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
}

.price-option-btn.is-blocked {
  opacity: 0.5;
  cursor: not-allowed;
}
.price-option-btn.is-blocked:hover {
  border-color: #e5e7eb;
  background: white;
}
.price-option-btn .line-blocked {
  font-size: 0.75rem;
  color: #b91c1c;
}
.price-option-btn.is-selected .price-label {
  color: #dc2626;
}

.price-option-btn.is-selected .price-amount {
  color: #dc2626;
}

/* Imagen ampliada (fixed: no la recorta el scroll del modal de orden) */
.image-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.6);
  outline: none;
}

.image-preview-dialog {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: white;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.image-preview-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 12px 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.image-preview-title {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  line-height: 1.3;
}

.image-preview-code {
  font-weight: 700;
  color: #111827;
}

.image-preview-description {
  color: #6b7280;
  overflow-wrap: anywhere;
}

.image-preview-close {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  font-size: 22px;
  color: #6b7280;
}

.image-preview-close:hover {
  background: #f3f4f6;
  color: #111827;
}

.image-preview-img {
  flex: 1;
  min-height: 0;
  width: 100%;
  max-height: calc(100vh - 140px);
  padding: 16px;
  object-fit: contain;
}
</style>

<!-- Global styles for modal (unscoped for iOS Safari body append) -->
<style>
/* Mobile Edit Modal - Full Screen on Mobile */
.mobile-edit-modal-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2147483647 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  isolation: isolate;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

.mobile-edit-modal-content {
  background: white;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Linea y precio: apilados; en pantalla grande, dos columnas */
.edit-modal-top {
  display: grid;
  gap: 1rem;
}
@media (min-width: 1024px) {
  .edit-modal-top.has-lines {
    grid-template-columns: 1fr 1fr;
    align-items: start;
    column-gap: 1.5rem;
  }
}

/* Desktop: centered modal (iPad Pro landscape and larger) */
@media (min-width: 1280px) {
  .mobile-edit-modal-content {
    width: 90%;
    max-width: 500px;
    height: auto;
    max-height: 90vh;
    border-radius: 12px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  }
  /* Con selector de linea el modal se ensancha para las dos columnas */
  .mobile-edit-modal-content.has-lines {
    max-width: 1000px;
  }
}

/* Ensure inputs don't zoom on iOS */
.mobile-edit-modal-content input[type="number"] {
  font-size: 16px;
}

/* Resumen de cambios: panel en escritorio, pantalla completa en movil */
#products-changes-modal .modal-content {
  width: 640px;
  max-width: 95vw;
  max-height: 85vh;
}

@media (max-width: 767px) {
  #products-changes-modal.modal {
    padding: 0;
  }

  #products-changes-modal .modal-content {
    width: 100%;
    max-width: none;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }

  #products-changes-modal .modal-content > .header .close {
    top: 50%;
    right: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    transform: translateY(-50%);
    font-size: 32px;
    color: #374151;
  }
}
.coupon-discount-cell {
  color: #047857;
  font-weight: 600;
  white-space: nowrap;
}
.coupon-head-code {
  display: block;
  color: #047857;
  font-size: 0.65rem;
  letter-spacing: 0.03em;
}
</style>
